import { StyledEngineProvider } from '@mui/material';
import { withProfiler } from '@sentry/react';
import { createBrowserHistory } from 'history';
import { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Router } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import Delay from './components/Delay';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalLoading from './components/GlobalLoading';
import { NowContext } from './components/Now';
import RecoilObserver from './components/RecoilObserver';
import ScrollToTop from './components/ScrollToTop';
import './i18n';
import { useLicense } from './license';
import { initSentry } from './sentry';
import { ThemeProvider } from './theme';
import { useTracking } from './tracking';

const history = createBrowserHistory();

initSentry({ history });

const Main = lazy(() => import('./Main'));
const Welcome = lazy(() => import('./pages/Welcome'));

const fallback = (
  <Delay>
    <GlobalLoading />
  </Delay>
);

function App() {
  return (
    <RecoilRoot>
      <StyledEngineProvider injectFirst>
        <NowContext>
          <ThemeProvider>
            <ErrorBoundary global>
              <HelmetProvider>
                <RecoilObserver />
                <Router history={history}>
                  <Suspense fallback={fallback}>
                    <ScrollToTop />
                    <Content />
                  </Suspense>
                </Router>
              </HelmetProvider>
            </ErrorBoundary>
          </ThemeProvider>
        </NowContext>
      </StyledEngineProvider>
    </RecoilRoot>
  );
}

function Content() {
  useTracking();
  const license = useLicense();

  return license ? <Main /> : <Welcome />;
}

export default withProfiler(App);
